import { useCallback, useEffect, useRef, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase';
import { readAccess } from './auth-security';
import { accountRequest } from './account-api';

export default function useSecureSession() {
  const [session,setSession]=useState({user:null,loading:true,error:'',access:{allowed:false,verified:false,graceUntil:0}});
  const current=useRef(null),generation=useRef(0);
  const refresh=useCallback(async()=>{
    const user=current.current,version=++generation.current;
    if(!user){setSession({user:null,loading:false,error:'',access:{allowed:false,verified:false,graceUntil:0}});return;}
    try {
      let access=await readAccess(user);
      if(access.allowed){
        const membership=await accountRequest('/api/membership',{method:'POST'});
        const until=membership.expiresAt||membership.paidUntil;
        access={...access,allowed:membership.journalAccess===true,membershipRequired:membership.journalAccess!==true,membership,
          membershipUntil:Number.isSafeInteger(until)?Date.now()+Math.max(0,until-(membership.serverNow||Date.now())):0};
      }
      if(version===generation.current)setSession({user,loading:false,error:'',access});
    }catch {
      if(version===generation.current)setSession({user,loading:false,error:'We couldn’t confirm your sign-in. Check your connection and try again.',access:{allowed:false,verified:false,graceUntil:0}});
    }
  },[]);
  useEffect(()=>{
    const unsubscribe=onAuthStateChanged(auth,user=>{
      current.current=user;
      setSession({user,loading:!!user,error:'',access:{allowed:false,verified:false,graceUntil:0}});
      refresh();
    });
    const invalidate=()=>{generation.current++;};
    return()=>{invalidate();unsubscribe();};
  },[refresh]);
  useEffect(()=>{const changed=()=>refresh();window.addEventListener('tradelog-membership-changed',changed);return()=>window.removeEventListener('tradelog-membership-changed',changed);},[refresh]);
  useEffect(()=>{
    if(!session.access.graceUntil)return;
    const check=()=>{if(Date.now()>=session.access.graceUntil)setSession(previous=>({...previous,access:{...previous.access,allowed:false,graceUntil:0}}));};
    const timer=setInterval(check,1000);
    window.addEventListener('focus',check);
    return()=>{clearInterval(timer);window.removeEventListener('focus',check);};
  },[session.access.graceUntil]);
  useEffect(()=>{
    if(!session.user)return;
    const until=session.access.membershipUntil;
    const expire=()=>{if(until&&Date.now()>=until)setSession(previous=>({...previous,access:{...previous.access,allowed:false,membershipRequired:true,membership:{...previous.access.membership,journalAccess:false,expired:true}}}));};
    let expiry=null;
    const scheduleExpiry=()=>{const remaining=until-Date.now();if(remaining<=0){expire();return;}expiry=setTimeout(scheduleExpiry,Math.min(remaining,2147483647));};
    if(until)scheduleExpiry();
    const timer=setInterval(()=>{expire();if(document.visibilityState==='visible')refresh();},300000);
    let lastFocus=Date.now();
    const focus=()=>{expire();if(document.visibilityState==='visible'&&Date.now()-lastFocus>30000){lastFocus=Date.now();refresh();}};
    window.addEventListener('focus',focus);document.addEventListener('visibilitychange',focus);
    return()=>{clearTimeout(expiry);clearInterval(timer);window.removeEventListener('focus',focus);document.removeEventListener('visibilitychange',focus);};
  },[session.user,session.access.membershipUntil,refresh]);
  return {...session,refresh};
}
